// @flow
import * as Sentry from '@sentry/react';
import { clearSearchValue } from '../../utils/http';
import { conversations, guestUsers, supports, userCredits, users as avaUsers } from '../../services/api/ava';
import { users as saasUsers } from '../../services/api/saas';
import { createV1Socket, getSocketIfExist, createScribeSocket } from '../../utils/getSocket';
import { audioPlayer } from '../../utils/audio';
import { getDefaultRoomId, sendAccountUpdateMessage } from '../../utils/ws-v1';
import { logoutAndClean } from '../../utils/user';
import { handleScribeDashboardWsMessage } from '../scribe-dashboard';
import * as segment from '../../utils/segment';
import { analytics, authInstance } from '../../firebase';
import * as log from '../../utils/log';
import { getOS } from '../../utils';
import { updateScribeRemainingTime, updatePaidASRCreditTime } from '../firebase/index';
import { setOnlyInternalAudio } from '../index';
import { batch } from 'react-redux';
import { getUserOrganization } from '../user';

export const getBranchIOData = (data: any) => {
  return {
    type: 'GET_BRANCH_IO_DATA',
    content: data,
  };
};

export const updateUser = (user: any) => (dispatch: Function, getState: Function) => {
  const {
    scribeSession: { backendSupport, captioner },
  } = getState();
  if (backendSupport.includes('connectRequest')) {
    dispatch({
      type: 'USER_UPDATE',
      user: {
        ...user,
        avaId: captioner.avaId,
      },
    });
    const socket = getSocketIfExist();
    if (socket) {
      try {
        sendAccountUpdateMessage(socket);
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }
};

export const updateBoosting = (value: boolean) => (dispatch: Function) => {
  dispatch({
    type: 'BOOSTING',
    isBoosting: value,
  });
};

export const updateFullScreen = (value: boolean) => {
  return {
    type: 'FULL_SCREEN',
    value,
  };
};

export const setLoadingApp = (status: boolean) => ({
  type: 'LOADING_APP',
  status,
});

export const setSelectedCaptions = (value: string) => (dispatch: Function) => {
  dispatch({
    type: 'SET_SELECTED_CAPTIONS',
    value,
  });
};

export const resetSelectedCaptions = (paidASRCreditTime: number) => (dispatch: Function) => {
  dispatch({
    type: 'RESET_SELECTED_CAPTIONS',
    paidASRCreditTime,
  });
};

export const setAudioParamsSent = (value: string) => (dispatch: Function) => {
  dispatch({
    type: 'SET_AUDIO_PARAMS_SENT',
    value,
  });
};

export const updateElectronCaptionMode = (value: boolean) => (dispatch: Function) => {
  dispatch({
    type: 'ELECTRON_CAPTION_MODE',
    value,
  });
};

export const fetchUserProfile = (firebaseAuthUID: string, isAnonymous: boolean = false) => async (
  dispatch: Function,
  getState: Function
): Promise<Object> => {
  const avaId = localStorage.getItem('avaId');
  let response;
  if (isAnonymous) {
    if (!avaId) {
      // new guest user
      response = await guestUsers.createGuestProfile({
        roomId: firebaseAuthUID && getDefaultRoomId(firebaseAuthUID),
        firebaseAuthUID,
      });
    } else {
      // existing guest user
      response = await guestUsers.getGuestProfile({ avaId, firebaseAuthUID });
    }
  } else {
    if (!firebaseAuthUID) {
      // This can only be thrown if there is an error in our code, never due to
      // external circumstance.
      throw new Error("Can't fetch non-anonymous user-profile without firebase user");
    }
    try {
      // Despite the name, this function cannot currently (05/16/2022) create
      // a profile. The ava-backend cannot do that at all, only saas-backend can.
      // That's why if that fails with a 400 and a custom_code of 104, we need to
      // create the profile via saas-backend, and then re-fetch it through ava-backend
      // in order to have complete data. This is slow, but only affects new users,
      // and can be cleaned up if the ava-backend ever allows user creation.
      response = await avaUsers.getOrCreateProfile({ firebaseAuthUID });
    } catch (e) {
      if (!e.response || !e.response.data || e.response.data.custom_error_code !== 104 || e.response.status !== 400) {
        throw e;
      }
      // This is able to create a profile, but returns an incomplete set of feature flags
      await saasUsers.getOrCreateUser();
      // That's why we need to refetch the profile from avaUsers.
      response = await avaUsers.getOrCreateProfile({ firebaseAuthUID });
    }
  }
  const { user, support, wsUrl, scribeUrl } = response.data;
  dispatch({ type: 'FETCH_AVA_USER_SUCCESS', user });
  dispatch({ type: 'SCRIBE_DASHBOARD_WS_URL', scribeUrl });
  dispatch({ type: 'V1_WS_URL', wsUrl });
  dispatch({ type: 'FETCH_BACKEND_SUPPORT', support });
  return user;
};

export const fetchBackendSupport = () => async (dispatch: Function): Promise<any> => {
  try {
    const res = await supports.getSupports();
    const { support } = res.data;
    dispatch({
      type: 'FETCH_BACKEND_SUPPORT',
      support,
    });
  } catch (err) {
    Sentry.captureException(err);
  }
};

export const fetchRemainingCredits = (avaId: string) => async (dispatch: Function) => {
  try {
    const res = await userCredits.getUserCredits({ avaId, firebaseAuthUID: authInstance.currentUser.uid });
    let { scribeCredit, extraPremiumASRCredit, recurringPremiumASRCredit } = res.data;
    if (!scribeCredit || scribeCredit < 0) {
      scribeCredit = 0;
    }
    if (!extraPremiumASRCredit || extraPremiumASRCredit < 0) {
      extraPremiumASRCredit = 0;
    }
    if (!recurringPremiumASRCredit || recurringPremiumASRCredit < 0) {
      recurringPremiumASRCredit = 0;
    }
    dispatch(updateScribeRemainingTime(scribeCredit));
    dispatch(updatePaidASRCreditTime(extraPremiumASRCredit + recurringPremiumASRCredit));
  } catch (err) {
    Sentry.captureException(err);
  }
};

export const fetchAvaWebAppSession = (options: { request?: { avaName: string }, roomId?: string }) => async (
  dispatch: Function,
  getState: Function
): Promise<any> => {
  const {
    firebase,
    scribeConversation: { token, status, scribe },
    scribeSession: { branch, avaUser: avaUserFromState, wsUrl: wsUrlFromState, backendSupport },
    firebase: {
      user: { uid, isAnonymous },
    },
  } = getState();

  // TODO:
  // During reconnection fetchAvaWebSession is called without calling fetchUserProfile
  // first. This leads to a situation in which wsUrl is stale.
  // How this should be fixed: all of the code responsible for Ava loading
  // (starting with fetchUserProfile) should live in load.js, and be callable by
  // both Ava code, and Firebase login flow. Until it's done, this hack makes
  // sure that the user is refetched in case the wsUrl is missing.
  let avaUser = avaUserFromState;
  let wsUrl = wsUrlFromState;
  if (!avaUserFromState || !wsUrlFromState) {
    avaUser = await fetchUserProfile(uid, isAnonymous)(dispatch, getState);
    wsUrl = getState().scribeSession.wsUrl;
  }

  const { request } = options;
  try {
    let roomId = status ? status.id : undefined;
    if (token && !roomId) {
      if (sessionStorage.getItem(token)) {
        roomId = sessionStorage.getItem(token);
      } else {
        try {
          const res = await conversations.getRoomId({ token });
          roomId = res.data.roomId;
          sessionStorage.setItem(token, roomId);
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    }
    if (!roomId && branch && (branch.roomId || branch.convoChannel)) {
      // This is an invite, let's track it
      analytics.logEvent('app_downloaded', {
        referrer_ava_id: branch.avaName,
        invite_channel: branch['~channel'],
        invite_campaign: branch['~campaign'],
      });
      segment.track('Invites Triggered', {
        'Invite Link': branch['~referring_link'],
        'Inviter Ava Name': branch.avaName,
      });
      segment.identify({
        'Invite Channel': branch['~channel'],
        'Invite Campaign': branch['~campaign'],
      });
      const metricsToUpdate = {
        'Total Invites Triggered Count': 1,
      };

      await avaUsers.updateInviteMetrics({
        avaId: avaUser.parse.avaId,
        firebaseAuthUID: avaUser.parse.firebaseAuthUID || avaUser.uid,
        inviteMetrics: metricsToUpdate,
      });
      roomId = branch.roomId || branch.convoChannel;
    }
    await createV1Socket(wsUrl, avaUser, {
      roomId: roomId || getDefaultRoomId(avaUser.parse ? `${avaUser.parse.avaId}` : avaUser.uid),
      request: (backendSupport || []).includes('connectRequest') ? request : undefined,
      scribe,
    });
    // It seems from the backend behavior that wsUrls are one-time use only, so it
    // should be cleared after it has been consumed.
    dispatch({ type: 'V1_WS_URL', wsUrl: '' });
    log.updateUserInfo({
      name: avaUser.parse.userName,
      avaName: avaUser.parse.avaName,
      firebaseAuthUid: avaUser.parse.firebaseAuthUID,
      avaId: avaUser.parse.avaId,
    });

    dispatch({
      type: 'FETCH_AVA_SESSION_SUCCESS',
      wsUrl,
      avaId: avaUser.parse.avaId,
      roomId,
      captioner: {
        id: avaUser.uid,
        username: avaUser.parse.avaName,
        name: avaUser.parse.userName,
        features: avaUser.features,
        profile: avaUser.profile,
        ...avaUser.parse,
      },
      support: backendSupport,
      photoUrl: avaUser.parse.userPhoto ? avaUser.parse.userPhoto.url : '',
    });

    if (!isAnonymous) {
      dispatch(getUserOrganization());
    }

    if (window.electronIPC) {
      window.electronIPC.sendSetIsLoggedIn(!!avaUser);
    }
  } catch (err) {
    const { response } = err;

    if (response && response.status < 500) {
      // eslint-disable-next-line no-alert
      alert('Unexpected error. Please contact support@ava.me if it happens again.');
      return logoutAndClean();
    }

    Sentry.captureException(err);
    dispatch({
      type: 'FETCH_AVA_SESSION_ERROR',
      error: {
        name: 'Lost connection',
        message: 'Try to refresh this page or verify your internet connection.',
      },
    });
    throw err;
  }
};

export const toggleDevMode = () => ({ type: 'TOGGLE_DEV_MODE' });
export const enableAudioMode = () => ({ type: 'ENABLE_AUDIO_MODE' });
export const toggleAudioMode = () => ({ type: 'TOGGLE_AUDIO_MODE' });
export const setAuthenticating = (value: boolean) => ({ type: 'SET_AUTHENTICATING', value });
export const setElectronAppLoaded = (value: boolean) => ({ type: 'ELECTRON_APP_LOADED', value });

export const toggleSideBar = (value?: boolean) => ({
  type: 'TOGGLE_SIDE_BAR',
  value,
});

export const exitScribeMode = () => {
  clearSearchValue(window, 'scribe');
  return { type: 'EXIT_SCRIBE_MODE' };
};

export const updateConversationEndedStatus = (status: boolean) => {
  if (status) {
    clearSearchValue(window, 'token');
    clearSearchValue(window, 'endpoint');
  }
  return { type: 'UPDATE_CONVERSATION_ENDED_STATUS', status };
};

export const setUrlToOpenAfterStartingConvo = (url?: string) => ({
  type: 'SET_URL_TO_OPEN_WITH_CONVO',
  url,
});

const handleConvoStatusChange = (state, newInConvo, dispatch) => {
  if (newInConvo === state.scribeSession.isInConversation) return;
  if (window.isElectron && getOS() === 'Mac') {
    const micId = state.scribeConversation.micIdSelected;
    const mic = micId && state.scribeConversation.availableMics.find(m => m.value === micId);
    const isInternal = mic && mic.label.startsWith('Ava Computer Audio');
    if (isInternal) {
      if (newInConvo) {
        window.electronIPC.sendActivateMacInternalAudio();
      } else {
        window.electronIPC.sendDeactivateMacInternalAudio();
      }
    }
  }
  if (state.scribeSession.urlToOpenAfterStartingConvo && newInConvo) {
    const url = state.scribeSession.urlToOpenAfterStartingConvo;
    setTimeout(() => {
      if (window.electronShell) {
        window.electronShell.openExternal(url);
      }
    }, 500);
    dispatch(setUrlToOpenAfterStartingConvo(undefined));
  }
};

export const updateIsInConversation = (value: boolean) => (dispatch: Function, getState: Function) => {
  const state = getState();
  if (window.electronIPC) {
    window.electronIPC.sendSetIsInConversation(value);
  }
  batch(() => {
    dispatch({
      type: 'UPDATE_IS_IN_CONVERSATION',
      value,
    });
    if (value) {
      dispatch(resetSelectedCaptions(state.firebase.paidASRCreditTime));
    }

    // If electron app and hearing profile is "Deaf non-voicing" - enable only internal audio
    // FIXME: when TS is introduced, this value should be in enum not hard coded
    if (value && window.isElectron && state.scribeSession.captioner?.profile?.hearingProfile === 4) {
      dispatch(setOnlyInternalAudio(true));
    }
  });

  handleConvoStatusChange(state, value, dispatch);
};

export const fetchAsyncBackendIfNeeded = () => async (dispatch: Function, getState: Function) => {
  const {
    scribeSession: { asyncBackends },
  } = getState();
  if (asyncBackends === 'none') {
    dispatch({ type: 'ASYNC_BACKEND_ENDPOINT', status: 'pending' });
    const { endpoints } = await (
      await fetch('https://5ebpkaja6a.execute-api.us-west-2.amazonaws.com/default/get-endpoints')
    ).json();
    dispatch({ type: 'ASYNC_BACKEND_ENDPOINT', endpoints, status: 'fetched' });
  }
};

export const setMacAudioSetup = () => ({
  type: 'MAC_AUDIO_SETUP',
});

export const redirectTo = (to: { pathname: string, search?: string }) => ({
  type: 'REDIRECT_TO',
  to,
});

export const clearRedirect = () => ({
  type: 'CLEAR_REDIRECT',
});

export const loggingOut = () => ({ type: 'LOGGING_OUT' });

export const setShowQuickSetup = (show: boolean) => ({
  type: 'SET_SHOW_QUICK_SETUP',
  show,
});

if (window.isElectron) {
  window.electronIPC.onDesktopAppLoaded(async () => {
    window.store.dispatch(setElectronAppLoaded(true));
  });
}

export const fetchAvaProfileMetrics = (updatedMetrics: Object) => ({
  type: 'FETCH_AVA_PROFILE_METRICS',
  metrics: updatedMetrics,
});
